import {Color} from '@benchling/nucleus-ui';
import {createStyles, Text, Tooltip} from '@mantine/core';
import React from 'react';

import useIsTruncated from 'ui-library/hooks/useIsTruncated';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';

import {
  type VaryingInputRow,
  type VaryingInputSection,
} from '../../procedure-runs/design-tabs/FactorVariationUtils';

const NO_SETPOINT_INDICATOR = '-';

interface ProcedureRunDesignFlowchartNodeSectionsProps {
  /** True when the parent node is either archived or pending archive */
  isInert: boolean;
  /** Any varying input sections to be rendered (if omitted, no sections will be rendered) */
  varyingInputSections?: Array<VaryingInputSection>;
}

export default function ProcedureRunDesignFlowchartNodeSections({
  isInert,
  varyingInputSections,
}: ProcedureRunDesignFlowchartNodeSectionsProps): JSX.Element | null {
  const {classes, cx} = useStyles();
  const sections = varyingInputSections ?? [];
  const shouldRenderVaryingInputSections = sections.length > 0;

  if (!shouldRenderVaryingInputSections) {
    return null;
  }

  return (
    <BenchlingNode.Sections spacing={0}>
      {sections.map((section) => (
        <BenchlingNode.Section key={section.title} leftHeader={section.title}>
          {section.rows.map((row, rowIndex) => {
            const displayValue = getDisplayValue(row);
            const isNoSetpointIndicator = getIsNoSetpointIndicator(row);
            return (
              <BenchlingNode.SectionRow
                key={`${section.title}-${row.label}-${rowIndex}`}
                align="flex-start"
                className={classes.varyingInputRow}
                noWrap={true}
                position="apart"
                spacing="xs"
              >
                <EllipsisTooltip
                  className={cx(classes.varyingInputLabel, isInert && classes.archivedText)}
                  value={row.label}
                />
                <EllipsisTooltip
                  aria-label={isNoSetpointIndicator ? 'No setpoint' : undefined}
                  className={cx(
                    classes.varyingInputValue,
                    isNoSetpointIndicator && classes.noSetpointIndicator,
                    isInert && classes.archivedText
                  )}
                  title={isNoSetpointIndicator ? 'No setpoint' : undefined}
                  tooltipValue={isNoSetpointIndicator ? 'No setpoint' : displayValue}
                  value={displayValue}
                />
              </BenchlingNode.SectionRow>
            );
          })}
        </BenchlingNode.Section>
      ))}
    </BenchlingNode.Sections>
  );
}

function getIsNoSetpointIndicator(row: VaryingInputRow): boolean {
  return row.value === '' && row.matchesEmptySetpoint === true;
}

function getDisplayValue(row: VaryingInputRow): string {
  if (getIsNoSetpointIndicator(row)) {
    return NO_SETPOINT_INDICATOR;
  }
  return row.value;
}

interface EllipsisTooltipProps {
  'aria-label'?: string;
  className: string;
  title?: string;
  tooltipValue?: string;
  value: string;
}

function EllipsisTooltip({
  'aria-label': ariaLabel,
  className,
  title,
  tooltipValue,
  value,
}: EllipsisTooltipProps): JSX.Element {
  const {ref, isTruncated} = useIsTruncated<HTMLDivElement>([value]);

  return (
    <Tooltip
      disabled={!isTruncated}
      events={{focus: true, hover: true, touch: false}}
      label={tooltipValue ?? value}
      withinPortal={true}
    >
      <Text
        ref={ref}
        aria-label={ariaLabel}
        className={className}
        tabIndex={isTruncated ? 0 : undefined}
        title={title}
      >
        {value}
      </Text>
    </Tooltip>
  );
}

const useStyles = createStyles((theme) => ({
  archivedText: {
    color: Color.GRAY_600,
  },
  varyingInputRow: {
    marginTop: theme.spacing.xs,
    marginBottom: theme.spacing.xs,
  },
  varyingInputLabel: {
    flex: 1,
    minWidth: 0,
    color: Color.GRAY_900,
    fontSize: theme.fontSizes.xs,
    lineHeight: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  varyingInputValue: {
    flexShrink: 0,
    maxWidth: 96,
    color: Color.GRAY_700,
    fontSize: theme.fontSizes.xs,
    lineHeight: '16px',
    overflow: 'hidden',
    textAlign: 'right',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  noSetpointIndicator: {
    color: Color.GRAY_600,
  },
}));
